import React from 'react';
import {Dispatch, FC, SetStateAction, useState} from 'react';
import {RxUpdate} from 'react-icons/rx';
import {
	containsObscenities,
	getDebug,
	isMasterFirebaseUser,
} from '../../library/GeneralUse';
import {
	hoursSinceLastUsernameUpdate,
	syncZustandFirestoreUser,
	updateUsername,
} from '../../library/FirestoreUser';
import {refreshUsersDocuments} from '../../library/UsersCollection';
import {useFirebaseAuthUserStore} from '../../stores/FirebaseAuthUserStore';
import {useFirestoreUserStore} from '../../stores/FirestoreUserStore';

const style = {
	submitButton: `flex basis-1/6 justify-center items-center   p-2   bg-whirlwindPrimary hover:bg-whirlwindPrimaryDark   text-whirlwindLightest hover:text-whirlwindLight   rounded   opacity-90`,
	submitButtonDisabled: `flex basis-1/6 justify-center items-center   p-2   text-whirlwindLight   bg-whirlwindDisabled   rounded   opacity-65`,
	textInput: `flex basis-5/6   p-4   text-xl text-whirlwindLightest   bg-whirlwindDarkest   rounded-md   opacity-90`,
	usernameForm: `flex flex-row justify-between gap-x-4   p-4`,
};

type UsernameFormProps = {
	setCurrentUsername: Dispatch<SetStateAction<null | string>>;
	setValidUsername: Dispatch<SetStateAction<boolean>>;
};

const DEBUG = getDebug('users');

export const UsernameForm: FC<UsernameFormProps> = ({
	setCurrentUsername,
	setValidUsername,
}) => {
	// Zustand
	const firebaseAuthUser = useFirebaseAuthUserStore(
		(state) => state.firebaseAuthUser,
	);
	const firestoreUser = useFirestoreUserStore((state) => state.firestoreUser);

	// Component variables
	const [buttonDisabled, setButtonDisabled] = useState(false);

	// Component methods
	const handleUsernameFormSubmit = async () => {
		DEBUG && console.clear();
		DEBUG && console.log(`handleUsernameFormSubmit()`);

		try {
			if (firebaseAuthUser) {
				const usernameFormTextInput = document.getElementById(
					'usernameFormText',
				) as HTMLInputElement;
				const newUsername = usernameFormTextInput.value;

				if (firestoreUser) {
					if (
						firestoreUser?.username !== newUsername &&
						!(firestoreUser?.username === null && newUsername === '')
					) {
						if (
							isMasterFirebaseUser(firebaseAuthUser.uid) ||
							hoursSinceLastUsernameUpdate() > 24 ||
							isNaN(hoursSinceLastUsernameUpdate())
						) {
							if (!containsObscenities(newUsername)) {
								await updateUsername(newUsername);
								await syncZustandFirestoreUser();
								setCurrentUsername(
									newUsername === '' ? null : newUsername,
								);
								setValidUsername(true);

								await refreshUsersDocuments(16);
							} else {
								DEBUG && console.log(`Not an allowed username`);
								setValidUsername(false);
							}
						} else {
							DEBUG &&
								console.log(
									`Not master Firebase user or it's been less than 24 hours since last username update or we have never updated username before`,
								);
						}
					} else {
						DEBUG && console.log(`We already have that username!`);
						setValidUsername(true);
					}
				} else {
					throw new Error(
						`We shouldn't be allowed to use this method (i.e. parent form) if we are not set in the firestoreUser!`,
					);
				}
			}
		} catch (error) {
			DEBUG && console.error(error);
		}
	};

	return (
		<form
			className={style.usernameForm}
			data-test-id="usernameForm_TEST"
			id="usernameForm"
			onSubmit={async (event) => {
				event.preventDefault();
				setButtonDisabled(true);
				await handleUsernameFormSubmit();
				setButtonDisabled(false);
			}}>
			<input
				className={style.textInput}
				data-test-id="usernameFormText_TEST"
				defaultValue={firestoreUser?.username}
				id="usernameFormText"
				maxLength={15}
				placeholder="username"
				type="text"
			/>
			<button
				className={
					buttonDisabled ? style.submitButtonDisabled : style.submitButton
				}
				data-test-id="usernameFormButton_TEST"
				disabled={buttonDisabled}
				id="usernameFormButton"
				type="submit">
				<RxUpdate size={40} />
			</button>
		</form>
	);
};
